import { useCallback, useRef, useState } from "react";
import { useRuleResourceContext } from "../../../contexts/RuleResourceContext";
import { getAllConnectionKeys } from "../../../services/connectionKeysService";
import {
  IMixedSubQueryV2,
  createEmptySubQuery,
  DEFAULT_SUBQUERY_QUERY,
  SUBQUERY_DEFAULT_QUERIES,
  MERGE_SUB_TYPES,
  getNextDependencyMarker,
  getNextSubQueryId,
  findDanglingDependencyReferences,
  buildDanglingDependencyMessage,
} from "./mixedQueryV2.utils";

// Replaces the table token after FROM with the new table name, mirroring the
// legacy GenericMixedQueryTab.updateQueryTableName so External sub-queries keep
// the SQL query in sync as the user types the table name.
const updateQueryTableName = (query: string, newTable: string): string => {
  if (!query) return "";
  const regex = /(from\s+)(<[^<>]*>|\w*)/i;
  return query.replace(regex, `$1${newTable}`);
};

// Owns the sub-query (data source) editing state and all the field handlers,
// ported faithfully from the legacy GenericMixedQueryTab so behaviour matches.
// The presentational SubQueryCard consumes these handlers via props.
export interface UseMixedQueryV2StateReturn {
  subQueries: IMixedSubQueryV2[];
  setSubQueries: React.Dispatch<React.SetStateAction<IMixedSubQueryV2[]>>;
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  handleTypeChange: (index: number, value: string | null) => void;
  handleFieldChange: (index: number, field: string, value: any) => Promise<void>;
  handleLinkedServiceChange: (
    index: number,
    field: string,
    value: any,
  ) => Promise<void>;
  handleQueryChange: (index: number, value: string) => void;
  handleEditorFocus: (index: number) => void;
  handleEditorBlur: () => void;
  addSubQuery: () => void;
  removeSubQuery: (index: number) => void;
  handleToggleActive: (index: number) => void;
  getDependencyDisplayLabel: (marker: string) => string;
  handleSetExternalColumns: (
    index: number,
    cols: { name: string; value: string }[],
  ) => void;
  mixedErrors: MixedQueryV2Errors;
  setMixedErrors: React.Dispatch<React.SetStateAction<MixedQueryV2Errors>>;
  validateMixedQuery: (mainQuery: string, queryName: string) => boolean;
}

// Errors are keyed by sub-query index (0..n-1) and by -1 for the Main Query.
// Each entry holds field-level messages consumed by SubQueryCard (resource_id,
// linked_service_id, connection_key_id, external_source_name, table_name) and
// `query` for the SQL editor (data_pull_query / main query content).
export type MixedQueryV2FieldErrors = {
  resource_id?: string;
  linked_service_id?: string;
  connection_key_id?: string;
  external_source_name?: string;
  table_name?: string;
  query?: string;
};

// Top-level `name` mirrors the legacy mixedQueryNewSchema query-name validation.
export type MixedQueryV2Errors = {
  name?: string;
  [key: number]: MixedQueryV2FieldErrors;
};

interface UseMixedQueryV2StateArgs {
  columns: any;
  setColumns: React.Dispatch<React.SetStateAction<any>>;
  resourcesData: any[];
  maxSubQueries: number;
  onFetchResourceColumns?: (resourceId: number) => void;
}

const useMixedQueryV2State = ({
  columns,
  setColumns,
  resourcesData,
  maxSubQueries,
  onFetchResourceColumns,
}: UseMixedQueryV2StateArgs): UseMixedQueryV2StateReturn => {
  const {
    setQueryBuilderTempValue,
    fetchedConnectionKeys,
    setFetchedConnectionKeys,
    setGlobalVariables,
  } = useRuleResourceContext();

  const [subQueries, setSubQueries] = useState<IMixedSubQueryV2[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [mixedErrors, setMixedErrors] = useState<MixedQueryV2Errors>({});
  const lastFocusedRef = useRef<number | null>(null);
  // Markers removed during this editing session. Retained so a query still
  // referencing a deleted sub-query can be flagged on Save.
  const removedMarkersRef = useRef<string[]>([]);

  // Wraps the exposed setter so a wholesale replacement of the sub-queries
  // (hydrating a record for edit, or resetting the editor) also discards the
  // removed-marker history, which belongs to the previous editing session.
  const replaceSubQueries = useCallback<
    React.Dispatch<React.SetStateAction<IMixedSubQueryV2[]>>
  >((value) => {
    if (typeof value !== "function") {
      removedMarkersRef.current = [];
    }
    setSubQueries(value);
  }, []);

  const clearFocus = () => {
    lastFocusedRef.current = null;
  };

  // Clears a single field-level error for a sub-query (or the Main Query at
  // index -1) once the user supplies a valid value, so errors surfaced on Save
  // disappear live as the form is filled in rather than lingering until the
  // next Save attempt.
  const clearFieldError = useCallback(
    (index: number, field: keyof MixedQueryV2FieldErrors) => {
      setMixedErrors((prev) => {
        const entry = prev[index];
        if (!entry || entry[field] === undefined) return prev;
        const nextEntry = { ...entry };
        delete nextEntry[field];
        const next = { ...prev };
        if (Object.keys(nextEntry).length === 0) {
          delete next[index];
        } else {
          next[index] = nextEntry;
        }
        return next;
      });
    },
    [],
  );

  const handleTypeChange = useCallback(
    (index: number, value: string | null) => {
      // Comparison sub-types (Merged/Missing/Mismatched) seed a dedicated default
      // query pointing at the rule's magic result tables; all others fall back to
      // the generic "SELECT * FROM <>" template.
      const defaultQuery =
        (value && SUBQUERY_DEFAULT_QUERIES[value]) || DEFAULT_SUBQUERY_QUERY;
      setSubQueries((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          type: value || "",
          sub_type: (value as any) || "",
          resource_id: null,
          resource_code: null,
          linked_service_id: null,
          linked_service_code: null,
          connection_key_id: null,
          connection_key_code: null,
          table_name: "",
          external_source_name: "",
          connection_details: [],
          dependency_list: [],
          data_pull_query: defaultQuery,
        };
        return updated;
      });
      // Seed the shared `columns` with the rule-derived merged/mismatched column
      // sets when a comparison sub-type is selected (mirrors the legacy tab so
      // the merged column chips populate immediately).
      if (value && MERGE_SUB_TYPES.includes(value)) {
        setColumns((prev: any) => ({
          ...prev,
          mergedColumns:
            value === "Mismatched_Data"
              ? prev?.mismatchedColumns
              : prev?.missingColumns,
        }));
      }
    },
    [setColumns],
  );

  const handleFieldChange = useCallback(
    async (index: number, field: string, value: any): Promise<void> => {
      if (
        field === "connection_key_id" ||
        field === "table_name" ||
        field === "external_source_name"
      ) {
        clearFocus();
      }
      let nextTempQuery: string | null = null;
      setSubQueries((prev) => {
        const updated = [...prev];
        const entry = { ...updated[index] };
        if (field === "resource_id") {
          entry.resource_id = value?.id ?? null;
          entry.resource_code = value?.code ?? null;
          if (entry.sub_type === "Resource") {
            entry.data_pull_query = `SELECT * FROM ${
              value?.code ? `<${value.code}>` : ""
            }`;
          }
          const resourceVar = value?.additional_properties?.inline_variables;
          if (resourceVar) setGlobalVariables(resourceVar);
        } else if (field === "connection_key_id") {
          entry.connection_key_id = value?.id ?? null;
          entry.connection_key_code = value?.code ?? null;
        } else if (field === "table_name") {
          entry.table_name = value;
          if (entry.sub_type === "External") {
            entry.data_pull_query = updateQueryTableName(
              entry.data_pull_query || "",
              value,
            );
          }
        } else {
          (entry as any)[field] = value;
        }
        updated[index] = entry;
        if (field === "table_name" && entry.sub_type === "External") {
          nextTempQuery = entry.data_pull_query || "";
        }
        return updated;
      });
      if (nextTempQuery !== null) {
        setQueryBuilderTempValue(nextTempQuery);
      }
      // Clear the surfaced validation error for the field once a valid value is
      // supplied. resource_id / table_name also regenerate the data_pull_query,
      // so clear the query error alongside them.
      if (field === "resource_id" && value?.id) {
        clearFieldError(index, "resource_id");
        clearFieldError(index, "query");
      } else if (field === "connection_key_id" && value?.id) {
        clearFieldError(index, "connection_key_id");
      } else if (field === "table_name" && value?.trim?.()) {
        clearFieldError(index, "table_name");
        clearFieldError(index, "query");
      } else if (field === "external_source_name" && value?.trim?.()) {
        clearFieldError(index, "external_source_name");
      }
      if (field === "resource_id" && value?.id && onFetchResourceColumns) {
        onFetchResourceColumns(value.id);
      }
    },
    [
      clearFieldError,
      onFetchResourceColumns,
      setGlobalVariables,
      setQueryBuilderTempValue,
    ],
  );

  const handleLinkedServiceChange = useCallback(
    async (index: number, field: string, value: any): Promise<void> => {
      clearFocus();
      const valueId = value?.id ?? null;
      setSubQueries((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          [field]: valueId,
          connection_details: value?.connection_details?.connection_keys ?? [],
          linked_service_code: value?.code ?? null,
          connection_key_id: null,
        };
        return updated;
      });
      // Selecting a connection type clears its error; the dependent connection
      // key is reset, so its error (if any) is left to re-validate on Save.
      if (field === "linked_service_id" && valueId) {
        clearFieldError(index, "linked_service_id");
      }
      const keyIds = value?.connection_details?.connection_keys;
      if (keyIds?.length > 0) {
        try {
          const fetched = await getAllConnectionKeys(keyIds);
          if (fetched?.length > 0) {
            const existing = fetchedConnectionKeys || [];
            const merged = [
              ...existing,
              ...fetched.filter(
                (k: any) => !existing.some((ek: any) => ek.id === k.id),
              ),
            ];
            setFetchedConnectionKeys(merged);
          }
        } catch (err) {
          console.error("Error fetching connection keys:", err);
        }
      }
    },
    [clearFieldError, fetchedConnectionKeys, setFetchedConnectionKeys],
  );

  const handleQueryChange = useCallback(
    (index: number, value: string) => {
      setQueryBuilderTempValue(value);
      setSubQueries((prev) => {
        const updated = [...prev];
        if (updated[index]) {
          updated[index] = { ...updated[index], data_pull_query: value };
        }
        return updated;
      });
      if (value?.trim()) {
        clearFieldError(index, "query");
      }
    },
    [clearFieldError, setQueryBuilderTempValue],
  );

  const handleEditorFocus = useCallback(
    (index: number) => {
      lastFocusedRef.current = index;
      setActiveIndex(index);
      setQueryBuilderTempValue(subQueries[index]?.data_pull_query || "");
    },
    [subQueries, setQueryBuilderTempValue],
  );

  const handleEditorBlur = useCallback(() => {
    // Keep last focus in ref so column/variable inserts still target this editor.
  }, []);

  const addSubQuery = useCallback(() => {
    setSubQueries((prev) => {
      if (prev.length >= maxSubQueries) return prev;
      // The marker and the UI id are allocated independently of the current
      // length so deleting a middle sub-query and adding a new one can never
      // reuse an existing marker (DQ-8841).
      const next = createEmptySubQuery(
        getNextSubQueryId(prev),
        getNextDependencyMarker(prev),
      );
      const updated = [...prev, next];
      setActiveIndex(prev.length);
      return updated.map((item, index) => ({
        ...item,
        available_dependencies: updated
          .filter(
            (candidate, candidateIndex) =>
              candidateIndex !== index && candidate.is_active !== false,
          )
          .map((candidate) => candidate.dependency_marker),
      }));
    });
  }, [maxSubQueries]);

  const removeSubQuery = useCallback((index: number) => {
    setSubQueries((prev) => {
      const removedMarker = prev[index]?.dependency_marker;
      if (removedMarker && !removedMarkersRef.current.includes(removedMarker)) {
        removedMarkersRef.current = [
          ...removedMarkersRef.current,
          removedMarker,
        ];
      }
      const updated = prev.filter((_, i) => i !== index);
      // Recompute available_dependencies for each remaining item.
      return updated.map((it, i) => ({
        ...it,
        available_dependencies: updated
          .filter(
            (candidate, candidateIndex) =>
              candidateIndex !== i && candidate.is_active !== false,
          )
          .map((p) => p.dependency_marker),
        dependency_list: (it.dependency_list || []).filter((dep) =>
          updated.some(
            (candidate, candidateIndex) =>
              candidateIndex !== i &&
              candidate.is_active !== false &&
              candidate.dependency_marker === dep,
          ),
        ),
      }));
    });
    setActiveIndex((prev) => (prev >= index && prev > 0 ? prev - 1 : prev));
  }, []);

  // Toggle a sub-query's active/inactive state (ported from the legacy
  // GenericMixedQueryTab.handleToggleActive). Inactive sub-queries are removed
  // from every other sub-query's available_dependencies and dependency_list;
  // re-activating restores them to the dependency pool (the user re-adds them to
  // dependency chains manually).
  const handleToggleActive = useCallback((index: number) => {
    setSubQueries((prev) => {
      const toggled = prev.map((it, i) =>
        i === index ? { ...it, is_active: !it.is_active } : { ...it },
      );
      // Recompute each item's available dependencies as every other active
      // sub-query, and prune dependency_list to those markers.
      return toggled.map((it, i) => {
        const available = toggled
          .filter(
            (candidate, candidateIndex) =>
              candidateIndex !== i && candidate.is_active !== false,
          )
          .map((p) => p.dependency_marker);
        return {
          ...it,
          available_dependencies: available,
          dependency_list: (it.dependency_list || []).filter((dep) =>
            available.includes(dep),
          ),
        };
      });
    });
  }, []);

  const getDependencyDisplayLabel = useCallback(
    (marker: string): string => {
      const matched = subQueries.find((it) => it.dependency_marker === marker);
      if (!matched) return marker;
      if (matched.sub_type === "External") {
        return matched.external_source_name?.trim() || marker;
      }
      return (
        matched.external_source_name?.trim() ||
        matched.resource_code?.trim() ||
        marker
      );
    },
    [subQueries],
  );

  // Persist fetched External columns onto the sub-query item so each External
  // card is scoped to its own table (no cross-card bleed via shared state).
  const handleSetExternalColumns = useCallback(
    (index: number, cols: { name: string; value: string }[]) => {
      setSubQueries((prev) => {
        const updated = [...prev];
        if (updated[index]) {
          updated[index] = { ...updated[index], external_columns: cols };
        }
        return updated;
      });
    },
    [],
  );

  // Field-level validation for the Mixed Query V2 editor, mirroring the legacy
  // genericMixedQueryItemSchema / mixedQueryNewSchema. Populates `mixedErrors`
  // keyed by sub-query index (and -1 for the Main Query) so SubQueryCard and the
  // Main Query editor can surface field-level messages. Returns true when valid.
  const validateMixedQuery = useCallback(
    (mainQuery: string, queryName: string): boolean => {
      const nextErrors: MixedQueryV2Errors = {};

      if (!queryName?.trim()) {
        nextErrors.name = "Query name is required";
      }

      subQueries.forEach((item, index) => {
        const fieldErrors: MixedQueryV2FieldErrors = {};

        if (item.sub_type === "Resource") {
          if (item.resource_id === null || item.resource_id === undefined) {
            fieldErrors.resource_id = "Resource is required";
          }
        } else if (item.sub_type === "External") {
          if (
            item.linked_service_id === null ||
            item.linked_service_id === undefined
          ) {
            fieldErrors.linked_service_id = "Connection Type is required";
          }
          if (
            item.connection_key_id === null ||
            item.connection_key_id === undefined
          ) {
            fieldErrors.connection_key_id = "Connection Key ID is required";
          }
          if (!item.external_source_name?.trim()) {
            fieldErrors.external_source_name =
              "External source name is required";
          }
          // table_name is optional for External sources — it is only needed to
          // fetch columns, which is validated separately at fetch time.
        }
        // Comparison sub-types (Merged/Missing/Mismatched) intentionally skip the
        // branches above — they carry no resource/external fields and only require
        // a non-empty query, enforced by the shared check below.

        if (item.sub_type && !item.data_pull_query?.trim()) {
          fieldErrors.query = "Query is required";
        }

        if (Object.keys(fieldErrors).length > 0) {
          nextErrors[index] = fieldErrors;
        }
      });

      if (!mainQuery?.trim()) {
        nextErrors[-1] = { query: "Please enter query" };
      }

      // Deleting a sub-query preserves the remaining markers, so any query still
      // pointing at the deleted marker would fail at execution time. Surface it
      // on the owning editor (index -1 = Main Query) unless that editor already
      // has a query-level message.
      findDanglingDependencyReferences(
        subQueries,
        mainQuery,
        removedMarkersRef.current,
      ).forEach(({ index, markers }) => {
        const entry = nextErrors[index] || {};
        if (entry.query) return;
        nextErrors[index] = {
          ...entry,
          query: buildDanglingDependencyMessage(markers),
        };
      });

      setMixedErrors(nextErrors);
      return Object.keys(nextErrors).length === 0;
    },
    [subQueries],
  );

  return {
    subQueries,
    setSubQueries: replaceSubQueries,
    activeIndex,
    setActiveIndex,
    handleTypeChange,
    handleFieldChange,
    handleLinkedServiceChange,
    handleQueryChange,
    handleEditorFocus,
    handleEditorBlur,
    addSubQuery,
    removeSubQuery,
    handleToggleActive,
    getDependencyDisplayLabel,
    handleSetExternalColumns,
    mixedErrors,
    setMixedErrors,
    validateMixedQuery,
  };
};

export default useMixedQueryV2State;
